import React, { useEffect, useState } from 'react';
import { Alert, Button, Card, Skeleton, Tooltip, message } from 'antd';
import {
  ApiOutlined,
  CheckCircleFilled,
  CopyOutlined,
  DatabaseOutlined,
  EyeOutlined,
  FileSearchOutlined,
  StopOutlined,
} from '@ant-design/icons';
import api from '../services/api';
import styles from './McpAccess.module.css';

interface ConnectionInfo {
  url: string;
  configured: boolean;
  allowed: boolean;
  scope: string;
}

const EXAMPLES = [
  'Скільки було замовлень за тиждень і на яку суму?',
  'Які товари найпопулярніші цього місяця?',
  'Покажи клієнтів, які давно не замовляли',
  'Яких товарів мало на складі?',
  'Які промокоди зараз активні?',
];

const LIMITS = [
  {
    icon: <EyeOutlined />,
    title: 'Тільки читання',
    text: 'Створити замовлення, змінити ціну чи видалити щось Claude не може — таких інструментів немає, і база не дозволяє запис.',
  },
  {
    icon: <DatabaseOutlined />,
    title: 'Лише ваш магазин',
    text: 'Дані інших магазинів платформи недоступні.',
  },
  {
    icon: <FileSearchOutlined />,
    title: 'Кожен запит у журналі',
    text: 'Записується, хто, коли й що саме дивився.',
  },
  {
    icon: <StopOutlined />,
    title: 'Миттєве відкликання',
    text: 'Щойно акаунт деактивують — доступ зникає з наступного ж запиту.',
  },
];

/** Hand-to-a-colleague page: the address, how to connect, and what it can see. */
const McpAccess: React.FC = () => {
  const [info, setInfo] = useState<ConnectionInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState<string | null>(null);

  useEffect(() => {
    api
      .get('/admin/mcp/connection')
      .then(({ data }) => setInfo(data))
      .catch(() => message.error('Не вдалося завантажити дані підключення'))
      .finally(() => setLoading(false));
  }, []);

  const copy = async (text: string, id: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(id);
      setTimeout(() => setCopied((c) => (c === id ? null : c)), 1800);
    } catch {
      message.error('Не вдалося скопіювати — виділіть і скопіюйте вручну');
    }
  };

  if (loading) {
    return (
      <div className={styles.page}>
        <Card><Skeleton active paragraph={{ rows: 6 }} /></Card>
      </div>
    );
  }

  const url = info?.url || '';
  const cliCommand = `claude mcp add --transport http spotengine-admin ${url}`;

  const CopyField: React.FC<{ value: string; id: string; label: string }> = ({
    value,
    id,
    label,
  }) => (
    <div className={styles.field}>
      <div className={styles.fieldLabel}>{label}</div>
      <div className={styles.fieldRow}>
        <code className={styles.code}>{value}</code>
        <Tooltip title={copied === id ? 'Скопійовано' : 'Копіювати'}>
          <Button
            type={copied === id ? 'primary' : 'default'}
            icon={copied === id ? <CheckCircleFilled /> : <CopyOutlined />}
            onClick={() => copy(value, id)}
            className={styles.copyBtn}
          >
            {copied === id ? 'Готово' : 'Копіювати'}
          </Button>
        </Tooltip>
      </div>
    </div>
  );

  return (
    <div className={styles.page}>
      <header className={styles.hero}>
        <div className={styles.heroIcon}><ApiOutlined /></div>
        <div className={styles.heroText}>
          <h1 className={styles.title}>Claude у вашому магазині</h1>
          <p className={styles.sub}>
            Підключіть Claude — і питайте про замовлення, клієнтів, залишки та
            статистику звичайною мовою, не відкриваючи адмінку.
          </p>
        </div>
        {info?.configured && info.allowed && (
          <div className={styles.badge}>
            <CheckCircleFilled /> Доступ відкрито
          </div>
        )}
      </header>

      {!info?.configured && (
        <Alert
          className={styles.alert}
          type="warning"
          showIcon
          message="Підключення ще не налаштоване"
          description="У цьому середовищі адміністративний MCP не увімкнено. Зверніться до розробників."
        />
      )}

      {info?.configured && !info.allowed && (
        <Alert
          className={styles.alert}
          type="info"
          showIcon
          message="У вашого акаунта немає доступу"
          description="Підключати Claude можуть власники та адміністратори магазину. Попросіть власника змінити роль, якщо доступ потрібен."
        />
      )}

      {info?.configured && (
        <div className={styles.grid}>
          <Card className={styles.card} title="Як підключити">
            <CopyField value={url} id="url" label="Адреса підключення" />

            <div className={styles.client}>
              <div className={styles.clientName}>Claude Desktop</div>
              <ol className={styles.steps}>
                <li>Settings → Connectors</li>
                <li>«Add custom connector»</li>
                <li>Вставте адресу вище і збережіть</li>
                <li>
                  Увійдіть логіном від адмінки
                  <span className={styles.note}>
                    Якщо адмінка вже відкрита — пароль не питатимуть, лише підтвердження
                  </span>
                </li>
              </ol>
            </div>

            <div className={styles.client}>
              <div className={styles.clientName}>Claude Code</div>
              <CopyField value={cliCommand} id="cli" label="Команда" />
              <p className={styles.note}>
                Далі введіть <code className={styles.inline}>/mcp</code> у сесії та
                натисніть «Authenticate»
              </p>
            </div>
          </Card>

          <div className={styles.side}>
            <Card className={styles.card} title="Що можна питати">
              <div className={styles.examples}>
                {EXAMPLES.map((q) => (
                  <button
                    key={q}
                    type="button"
                    className={styles.example}
                    onClick={() => copy(q, q)}
                    title="Скопіювати запит"
                  >
                    {copied === q ? <CheckCircleFilled className={styles.exampleOk} /> : null}
                    {q}
                  </button>
                ))}
              </div>
            </Card>

            <Card className={styles.card} title="Межі доступу">
              <ul className={styles.limits}>
                {LIMITS.map((l) => (
                  <li key={l.title} className={styles.limit}>
                    <span className={styles.limitIcon}>{l.icon}</span>
                    <span>
                      <span className={styles.limitTitle}>{l.title}</span>
                      <span className={styles.limitText}>{l.text}</span>
                    </span>
                  </li>
                ))}
              </ul>

              <Alert
                className={styles.warn}
                type="warning"
                showIcon
                message="Дані виходять за межі адмінки"
                description="Відповіді містять контакти клієнтів і потрапляють у Claude. Не підключайте на чужому чи спільному комп'ютері."
              />
            </Card>
          </div>
        </div>
      )}
    </div>
  );
};

export default McpAccess;
